const BLANK_DISPLAY = "—";

export const isUsableValue = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== "" && normalized !== "null" && normalized !== "undefined";
};

const parseJsonObject = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

export const resolveBillingSupplierName = (bill = {}) => {
  const info = bill?.billSupplierInfo;

  if (typeof info === "string") {
    const parsed = parseJsonObject(info);
    if (parsed) {
      return parsed.Name || parsed.name || parsed.supplierName || parsed.accountName || "";
    }
    return info;
  }

  if (info && typeof info === "object") {
    return info.Name || info.name || info.supplierName || info.accountName || "";
  }

  const billSupplierObj = parseJsonObject(bill?.billSupplier);

  return (
    billSupplierObj?.name ||
    billSupplierObj?.Name ||
    billSupplierObj?.accountName ||
    bill?.billSupplierName ||
    bill?.supplierName ||
    bill?.SupplierName ||
    bill?.supplier ||
    bill?.Supplier ||
    ""
  );
};

export const getShippingAccountDisplayName = (bill = {}) => {
  const info = bill?.billSupplierInfo && typeof bill.billSupplierInfo === "object"
    ? bill.billSupplierInfo
    : parseJsonObject(bill?.billSupplierInfo);
  const supplier = parseJsonObject(bill?.billSupplier);

  return (
    bill?.billPartyName ||
    info?.accountName ||
    info?.Name ||
    info?.name ||
    supplier?.accountName ||
    supplier?.Name ||
    supplier?.name ||
    ""
  );
};

export const isShippingAccountBill = (bill = {}) =>
  String(bill?.billPartyType || "").trim().toLowerCase() === "shipping-account";

export const hasAssociatedPurchaseOrder = (rowOrBill = {}) => {
  const bill = rowOrBill?.bill && typeof rowOrBill.bill === "object"
    ? rowOrBill.bill
    : rowOrBill;
  const groupKey = rowOrBill?.po;

  return Boolean(
    isUsableValue(bill?.poNumber) ||
    isUsableValue(bill?._poId) ||
    (isUsableValue(groupKey) && !String(groupKey).startsWith("bill:"))
  );
};

export const buildBillGroupKey = (bill = {}, index = 0) => {
  if (isUsableValue(bill?.poNumber)) return String(bill.poNumber).trim();
  if (isUsableValue(bill?._poId)) return `po:${bill._poId}`;
  if (isUsableValue(bill?.id)) return `bill:${bill.id}`;
  return `bill:standalone:${index}`;
};

const numericOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const firstUsable = (...values) => {
  for (const value of values) {
    if (isUsableValue(value)) return value;
  }
  return null;
};

export const resolveBillingPoContext = (row = {}) => {
  const bill = row?.bill || row || {};
  const groupKey = String(row?.po || "").trim();
  const hasPurchaseOrder = hasAssociatedPurchaseOrder(row);

  if (!hasPurchaseOrder) {
    return {
      hasPurchaseOrder: false,
      purchaseOrderId: null,
      poNumber: null,
      poStatus: null,
      poParty: isShippingAccountBill(bill)
        ? (getShippingAccountDisplayName(bill) || "Shipping")
        : null,
      poTarget: null,
      source: "standalone",
    };
  }

  const purchaseOrderId = firstUsable(bill?._poId, bill?.purchaseOrderId, bill?.PurchaseOrderId);
  const poNumber = firstUsable(
    bill?._poNumber,
    bill?.poNumber,
    !groupKey.startsWith("bill:") && !groupKey.startsWith("po:") ? groupKey : null
  );
  const poStatus = firstUsable(bill?._poStatus, bill?.poStatus, row?.poStatus);
  const poParty = firstUsable(bill?._poPartyName, bill?.poPartyName, bill?.supplierInfo);
  const targetFromPurchaseOrder = numericOrNull(bill?._poTargetAmount);
  const targetFromSeed = numericOrNull(bill?._seedPoAmount);
  const targetFromLegacyBilling = numericOrNull(bill?.poAmount);

  if (purchaseOrderId || targetFromPurchaseOrder !== null || bill?._poSource === "purchase-order") {
    return {
      hasPurchaseOrder: true,
      purchaseOrderId: purchaseOrderId || null,
      poNumber: poNumber || null,
      poStatus: poStatus || null,
      poParty: poParty || null,
      poTarget: targetFromPurchaseOrder ?? targetFromSeed ?? targetFromLegacyBilling,
      source: targetFromPurchaseOrder !== null || purchaseOrderId ? "purchase-order" : "seed",
    };
  }

  if (targetFromSeed !== null) {
    return {
      hasPurchaseOrder: true,
      purchaseOrderId: null,
      poNumber: poNumber || null,
      poStatus: poStatus || null,
      poParty: poParty || null,
      poTarget: targetFromSeed,
      source: "seed",
    };
  }

  return {
    hasPurchaseOrder: true,
    purchaseOrderId: null,
    poNumber: poNumber || null,
    poStatus: poStatus || null,
    poParty: poParty || null,
    poTarget: targetFromLegacyBilling,
    source: "legacy-billing",
  };
};

const PO_CONTEXT_SOURCE_RANK = {
  "purchase-order": 0,
  seed: 1,
  "legacy-billing": 2,
  standalone: 3,
};

export const resolveBillingPoGroupContext = (po, bills = []) => {
  const contexts = (Array.isArray(bills) ? bills : [])
    .map((bill) => resolveBillingPoContext({ po, bill }));
  return contexts.sort((a, b) =>
    (PO_CONTEXT_SOURCE_RANK[a.source] ?? 99) - (PO_CONTEXT_SOURCE_RANK[b.source] ?? 99)
  )[0] || resolveBillingPoContext({ po, bill: {} });
};

export const getDisplayPoNumber = (row = {}) => {
  const context = resolveBillingPoContext(row);
  return context.poNumber || BLANK_DISPLAY;
};

export const getDisplayPoStatus = (row = {}) =>
  resolveBillingPoContext(row).poStatus || "";

export const getDisplayPoParty = (row = {}) => {
  const bill = row?.bill || row;
  const context = resolveBillingPoContext(row);
  if (context.hasPurchaseOrder) return context.poParty || BLANK_DISPLAY;
  return context.poParty || (isShippingAccountBill(bill) ? "Shipping" : BLANK_DISPLAY);
};

export const getDisplayPoAmount = (row = {}, moneyFormatter = (value) => String(value)) => {
  const context = resolveBillingPoContext(row);
  if (!context.hasPurchaseOrder || context.poTarget === null || context.poTarget === undefined) {
    return BLANK_DISPLAY;
  }
  return `CA$${moneyFormatter(context.poTarget)}`;
};

export const getDisplayBillParty = (bill = {}) =>
  bill?.billPartyName || resolveBillingSupplierName(bill) || bill?.poPartyName || BLANK_DISPLAY;

export const isAllBillsReceivedApplicable = (row = {}) =>
  Boolean(row?.bill?.id && hasAssociatedPurchaseOrder(row));

export const getExportAllReceivedValue = (row = {}, complete = false) =>
  isAllBillsReceivedApplicable(row) ? (complete ? "Yes" : "No") : "N/A";

export const getBillingSummaryCounts = (rows = [], groupCompleteByPo = {}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const poKeys = Array.from(new Set(
    safeRows
      .filter((row) => hasAssociatedPurchaseOrder(row))
      .map((row) => row.po)
  ));

  return {
    totalBills: safeRows.filter((row) => Boolean(row?.bill?.id)).length,
    totalPOs: poKeys.length,
    poDone: poKeys.reduce((count, po) => count + (groupCompleteByPo?.[po] ? 1 : 0), 0),
    poTotal: poKeys.length,
  };
};

export const buildBillingExportRow = (
  row = {},
  {
    groupComplete = false,
    moneyFormatter = (value) => String(value),
    getInvoiceDisplay = () => ({ label: "" }),
    getBillSubtotal = (bill) => bill?.subTotal ?? bill?.amount ?? 0,
  } = {}
) => {
  const bill = row?.bill || {};
  const poNumber = getDisplayPoNumber(row);
  const poParty = getDisplayPoParty(row);
  const poAmount = getDisplayPoAmount(row, moneyFormatter);
  const billParty = getDisplayBillParty(bill);

  return {
    "PO #": poNumber === BLANK_DISPLAY ? "" : poNumber,
    "PO Status": getDisplayPoStatus(row),
    "PO Party": poParty === BLANK_DISPLAY ? "" : poParty,
    "PO Amount": poAmount === BLANK_DISPLAY ? "" : poAmount,
    "Invoice #": getInvoiceDisplay(bill).label,
    "Bill Party": billParty === BLANK_DISPLAY ? "" : billParty,
    "All Received": getExportAllReceivedValue(row, groupComplete),
    "Subtotal": bill.id ? `CA$${moneyFormatter(getBillSubtotal(bill))}` : "",
  };
};
